import React, { useCallback } from "react";
import { Typography, Box, TextField, CircularProgress } from "@mui/material";
import SectionSubheading from "./SectionSubheading";
import DimensionTable from "./DimensionTable";
import useRFIComponent from "../hooks/useRFIComponent";
import { Field, Form, Formik } from "formik";
import FileUploaderUI from "./ViewRFI/FileUploaderUI";
import theme from "../utils/theme";
import PaginationComponent from "./PaginationComponent";
import StageFooter from "./StageFooter";
import AddIcon from "@mui/icons-material/Add";
import AppButton from "./AppButton";
import DeleteConfirmationPopup from "./DeleteConfirmationPopup";
import CreateTemplateForm from "./Fabrication/CreateTemplateForm";
import StageInactive from "./ViewRFI/StageInactive";

const styles = {
  textField: {
    bgcolor: theme.palette.background.paper,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.grey[500],
      },
      "&.Mui-disabled": {
        "& fieldset": {
          borderColor: theme.palette.grey[500],
        },
      },
    },
    "& .MuiInputBase-input": {
      padding: "8px 10px",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.grey[500],
    },
  },
  addButton: {
    mb: 2,
    float: "right",
  },
};

/**
 * ComponentViewAndNDTmap - Component for viewing component details and NDT mapping
 */
const ComponentViewAndNDTmap = ({
  formEnabled,
  showAction,
  showSubmitButton,
  awaitingReview,
  isAPICallAllowed,
  stage,
  isActive,
}) => {
  const {
    components,
    loading,
    pagination,
    initialValues,
    validationSchema,
    currentComponent,
    mode,
    openFormDialog,
    deleteModal,
    deleteSelectedComponent,
    setDeleteModal,
    handleSubmitForm,
    handleSendForApproval,
    handleDocumentUpload,
    handleDocumentDelete,
    handlePageChange,
    handleEdit,
    handleCreate,
    handleDelete,
    setDeleteSelectedComponent,
    handleCloseDialog,
    handleSubmitCreateComponent,
  } = useRFIComponent({
    stageId: stage.stage_id,
    isAPICallAllowed: isAPICallAllowed,
  });

  const handleFilesSelect = useCallback(
    (files, setFieldValue, currentFiles) => {
      handleDocumentUpload(files, setFieldValue, currentFiles);
    },
    [handleDocumentUpload]
  );

  return (
    <>
      {isActive ? (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmitForm}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setTouched,
            handleBlur,
            submitForm,
            isSubmitting,
            handleChange,
          }) => (
            <Form>
              <Box sx={{ textAlign: "center" }}>
                <SectionSubheading title="Component View" sx={{ mb: 2 }} />
                {formEnabled && (
                  <AppButton
                    buttonType="primary"
                    onClick={handleCreate}
                    sx={styles.addButton}
                    label="Create New Component"
                    endIcon={<AddIcon />}
                  />
                )}
                {loading.components ? (
                  <CircularProgress />
                ) : (
                  <Box
                    sx={{ maxHeight: "70vh", overflowY: "auto", width: "100%" }}
                  >
                    {components?.length > 0 ? (
                      components.map((component, index) => (
                        <Box
                          key={component.component_id}
                          mb={2}
                          sx={{
                            width: "100%",
                          }}
                        >
                          <DimensionTable
                            lightHeader
                            items={component.items}
                            component_name={component.component_name}
                            images={component?.component_images || []}
                            showStagesFields={true}
                            formik={{
                              values,
                              errors,
                              touched,
                              setFieldValue,
                              setTouched,
                              handleBlur,
                              handleChange,
                            }}
                            disabled={!formEnabled}
                            s
                            onEdit={
                              formEnabled
                                ? () => handleEdit(component)
                                : undefined
                            }
                            onDelete={
                              formEnabled
                                ? () => {
                                    setDeleteSelectedComponent(component);
                                    setDeleteModal(true);
                                  }
                                : undefined
                            }
                            disableDelete={components?.length === 1}
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        No component data available
                      </Typography>
                    )}
                    {pagination.total > pagination.perPage && (
                      <PaginationComponent
                        pagination={pagination}
                        handlePageChange={handlePageChange}
                      />
                    )}
                  </Box>
                )}
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <SectionSubheading title="NDT Mapping" sx={{ mt: 4 }} />
                {loading.ndtMap ? (
                  <CircularProgress />
                ) : (
                  <Box>
                    <Field name="documents">
                      {({ field }) => (
                        <>
                          <FileUploaderUI
                            files={field.value}
                            onFilesSelect={(e) =>
                              handleFilesSelect(e, setFieldValue, field.value)
                            }
                            onFileDelete={(fileName) =>
                              handleDocumentDelete(
                                fileName,
                                setFieldValue,
                                field.value
                              )
                            }
                            acceptedFileTypes={[".pdf", ".doc", ".docx"]}
                            error={errors.documents}
                            touched={touched.documents}
                            inputProps={{ id: "document-upload-input" }}
                            title="Upload Documents"
                            viewOnly={!formEnabled}
                          />
                        </>
                      )}
                    </Field>

                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, textAlign: "left" }}
                      >
                        NDT Remarks
                      </Typography>
                      <TextField
                        id="remarks"
                        name="remarks"
                        value={values.remarks || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="outlined"
                        sx={styles.textField}
                        error={touched.remarks && Boolean(errors.remarks)}
                        helperText={touched.remarks && errors.remarks}
                        disabled={!formEnabled}
                        fullWidth
                      />
                    </Box>
                  </Box>
                )}
              </Box>
              <StageFooter
                showAction={showAction}
                showSubmitButton={showSubmitButton}
                awaitingReview={awaitingReview}
                onSendForApproval={
                  formEnabled &&
                  handleSendForApproval(setTouched, errors, submitForm)
                }
                isSubmitting={isSubmitting}
                stage={stage}
              />
            </Form>
          )}
        </Formik>
      ) : (
        <StageInactive />
      )}
      <DeleteConfirmationPopup
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        itemName={deleteSelectedComponent?.component_name || ""}
      />

      {/* Form Dialog */}
      <CreateTemplateForm
        open={openFormDialog}
        onClose={handleCloseDialog}
        onSave={handleSubmitCreateComponent}
        initialData={currentComponent}
        isEditing={mode === "edit"}
        title="Create Component"
      />
    </>
  );
};

export default ComponentViewAndNDTmap;
